import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Clock } from "lucide-react";

const POSTS = [
  {
    key: "fractional-startups",
    tag: "Fractional · SaaS / D2C",
    readTime: "8 min read",
    title:
      "Stop wasting ad spend: why your SaaS or D2C brand needs fractional leadership, not another agency",
    tagline:
      "Fix your broken growth engine with strategic fractional marketing leadership, not just execution.",
    summary:
      "Indian SaaS and D2C brands hit growth plateaus when they hire another execution-only agency. Fractional CMO leadership owns strategy, unit economics and team upskilling — for a fraction of a full-time hire.",
    bullets: [
      "Agencies execute. Fractional CMOs own the funnel.",
      "Move from vanity metrics to MER & ARR.",
      "Upskill in-house teams, don’t just hire.",
    ],
    image:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=70",
  },
  {
    key: "industrial-b2b",
    tag: "Industrial · B2B SMEs",
    readTime: "7 min read",
    title:
      "Beyond word-of-mouth: why Indian manufacturing & industrial SMEs need fractional marketing leadership",
    tagline:
      "Transform your Indian manufacturing SME with expert fractional marketing for B2B growth.",
    summary:
      "Industrial buyers now search online first. Traditional agencies lack the technical context to convert them. Fractional CMOs build inbound engines for high-intent leads — domestic and export.",
    bullets: [
      "Industrial B2B buyers research online first.",
      "Fractional CMOs build inbound lead engines.",
      "Unlock export demand with digital presence.",
    ],
    image:
      "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1200&q=70",
  },
  {
    key: "linkedin-sql-engine",
    tag: "B2B · LinkedIn",
    readTime: "9 min read",
    title:
      "The LinkedIn SQL engine: a framework for B2B pipelines that don’t freeze",
    tagline:
      "Build a high-intent B2B funnel on LinkedIn with account targeting, document ads and CRM enrichment.",
    summary:
      "Indian B2B SaaS founders see frozen pipelines despite paying premium LinkedIn CPCs. A SQL Engine framework — account-based targeting, document ads and multi-stage retargeting — fixes the funnel, not just the ad spend.",
    bullets: [
      "Treat LinkedIn like B2B, not B2C.",
      "Target seniority, not just job titles.",
      "Use document ads for ungated authority.",
    ],
    image:
      "https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=1200&q=70",
  },
  {
    key: "linkedin-ads",
    tag: "B2B · Paid",
    readTime: "8 min read",
    title:
      "Why your LinkedIn ad spend is freezing — and how to fix your SQL pipeline",
    tagline:
      "Stop wasting LinkedIn budget. Build a high-intent pipeline that actually converts.",
    summary:
      "LinkedIn ads failing for SaaS, fintech and enterprise services? Broad targeting and generic lead magnets are the root cause. Move to account-based motion, document ads and multi-stage retargeting tuned to long B2B cycles.",
    bullets: [
      "High CPCs, low-intent leads — fix the funnel.",
      "Document ads create frictionless trust.",
      "Layer retargeting for long B2B cycles.",
    ],
    image:
      "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1200&q=70",
  },
  {
    key: "meta-andromeda",
    tag: "B2C · Meta Ads",
    readTime: "6 min read",
    title: "Meta Andromeda AI: beat the 48-hour ad fatigue",
    tagline:
      "Master Meta’s new AI: unlock explosive growth with daily creative power.",
    summary:
      "Meta’s Andromeda AI is crushing ad shelf-life to ~48 hours. The teams winning are shipping high-velocity creatives daily, leaning into broad targeting, and letting creative do the filtering — not interests.",
    bullets: [
      "Ad shelf-life is now ~48 hours.",
      "Ship daily creative variants.",
      "Go broad. Let creative segment users.",
    ],
    image:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=70",
  },
];

export default function Resources() {
  return (
    <section
      id="resources"
      data-testid="resources-section"
      className="relative py-24 sm:py-32"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-yellow-400 font-mono-ui text-xs tracking-[0.22em] uppercase mb-4">
              · The Squeeze · Field notes
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl">
              Playbooks from the
              <br />
              <span className="brand-gradient-text">growth lab.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base sm:text-lg leading-relaxed">
            Short, sharp reads from the team — frameworks we actually run on
            client accounts, not regurgitated thought-leadership.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {POSTS.map((p, idx) => (
            <motion.article
              key={p.key}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.06 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/40 hover:border-yellow-400/40 transition-all flex flex-col"
              data-testid={`resource-card-${p.key}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-950/75 backdrop-blur border border-white/10 text-[11px] font-mono-ui text-yellow-300">
                  {p.tag}
                </div>
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/75 backdrop-blur border border-white/10 text-[11px] text-zinc-300">
                  <Clock size={11} /> {p.readTime}
                </div>
              </div>

              <div className="p-6 sm:p-7 flex flex-col flex-1">
                <h3 className="font-display text-lg sm:text-xl font-semibold leading-snug tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2.5 text-yellow-300 text-xs italic">
                  {p.tagline}
                </p>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
                  {p.summary}
                </p>

                <ul className="mt-5 space-y-2 flex-1">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-zinc-300 text-[13px] leading-relaxed"
                    >
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-yellow-400 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  data-testid={`resource-cta-${p.key}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-yellow-300 hover:text-yellow-200 transition-colors"
                >
                  Request the full playbook
                  <ArrowUpRight
                    size={16}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14 rounded-3xl border border-yellow-400/25 bg-gradient-to-br from-yellow-400/10 via-orange-500/5 to-transparent p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6"
          data-testid="resources-bundle-cta"
        >
          <div className="flex items-start gap-4 max-w-2xl">
            <span className="h-12 w-12 inline-flex items-center justify-center rounded-2xl bg-yellow-400/15 border border-yellow-400/30 text-yellow-300 shrink-0">
              <BookOpen size={22} />
            </span>
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Get the full playbook bundle.
              </h3>
              <p className="mt-2 text-zinc-300 text-sm sm:text-base leading-relaxed">
                All five field notes — fractional leadership, industrial B2B,
                the LinkedIn SQL engine and Meta Andromeda — delivered to your
                inbox. No fluff, no funnel.
              </p>
            </div>
          </div>
          <a
            href="#contact"
            data-testid="resources-bundle-button"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-yellow-400 text-zinc-950 font-bold hover:bg-yellow-300 transition-all hover:-translate-y-0.5 whitespace-nowrap"
          >
            Send me the bundle <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
